import { memo } from 'react';
import { Title } from 'react-admin';

import { Card, Table, TableContainer, TablePagination } from '@mui/material';

import {
  ActionInterface,
  ColumnInterface,
  GenericObject,
  GenericObjectWithActions,
} from '@app/components/common/Table/DefaultTable/types';

import { CustomActionPerRowTableBody } from '../CustomActionPerRowTableBody';
import { DefaultTableBody } from './DefaultTableBody';
import { DefaultTableHeader } from './DefaultTableHeader';
import './styles.css';

/**
 * Making the assumption that each row has the same format
 */
function eachRowHasIndividualActions(rows: GenericObject[] | GenericObjectWithActions[] | undefined) {
  if (!rows) {
    return false;
  }
  const singleRow = rows[0] as GenericObjectWithActions;
  if (!singleRow.actions) {
    return false;
  }
  return singleRow.actions.length > 0;
}

interface DefaultTableProps {
  title: string;
  columns: ColumnInterface[];
  rows: GenericObject[] | GenericObjectWithActions[] | undefined;
  actions?: ActionInterface[];
  orderable?: boolean;
  page?: number;
  resource?: string;
  rowsPerPage?: number;
  total?: number;
  noPadding?: boolean;
  handleChangePage?(event: React.MouseEvent<HTMLButtonElement> | null, page: number): void;
  handleChangeRowsPerPage?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  reorderRecord?(dragIndex: number, hoverIndex: number): void;
  isRowEditable?: Function;
  rowsPerPageOptions?: number[];
}

export const DefaultTable = memo(
  ({
    title,
    reorderRecord,
    orderable,
    rows,
    resource,
    columns,
    noPadding,
    actions,
    total,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    isRowEditable,
    rowsPerPageOptions,
  }: DefaultTableProps) => {
    const hasActions = (actions && actions.length > 0) || eachRowHasIndividualActions(rows);

    return (
      <div className={noPadding ? '' : 'default_table'}>
        <Title title={title} />
        <Card>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table sx={{ padding: 2 }} size="small" stickyHeader>
              <DefaultTableHeader columns={columns} orderable={orderable} hasActions={hasActions} />
              {actions ? (
                <DefaultTableBody
                  resource={resource}
                  reorderRecord={reorderRecord}
                  orderable={orderable}
                  rowKey="id"
                  rows={rows}
                  columns={columns}
                  actions={actions}
                  isRowEditable={isRowEditable}
                />
              ) : (
                <CustomActionPerRowTableBody
                  rowKey="id"
                  rowsWithActions={rows as GenericObjectWithActions[]}
                  columns={columns}
                />
              )}
            </Table>
          </TableContainer>
          {!orderable && (
            <TablePagination
              component="div"
              count={total ? total : 0}
              page={page ? page : 0}
              onPageChange={handleChangePage ? handleChangePage : () => {}}
              rowsPerPage={rowsPerPage ? rowsPerPage : 10}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={rowsPerPageOptions}
            />
          )}
        </Card>
      </div>
    );
  }
);
